import { ActionIcon, Button, type ComboboxItem, Select, Textarea } from '@any-ui-react/core';
import { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useListLanguagesQuery } from '@/common/graphql';
import { MarkdownTextarea, MarkdownTextareaMode } from '../markdown';
import { MaterialIcons } from '../material-icons';
import type { DefinitionFormItem } from './definitions-validation';

export interface DefinitionItem {
  languageCode: string;
  content: string;
}

export interface DefinitionsFieldProps {
  disabledButton: boolean;
  /** Edit the content with the Markdown editor (editor and preview tabs) instead of a plain textarea. */
  useMarkdown?: boolean;
}

export const DefinitionsField = ({ disabledButton, useMarkdown = false }: DefinitionsFieldProps) => {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<{ definitions: DefinitionFormItem[] }>();
  const { fields, append, remove } = useFieldArray({ control, name: 'definitions' });

  const { data: languagesData } = useListLanguagesQuery({
    variables: { pageNumber: null, pageSize: 200, keyword: null },
    fetchPolicy: 'cache-first',
  });

  const currentRows = (useWatch({ control, name: 'definitions' }) as DefinitionItem[] | undefined) ?? [];
  const selectedCodes = useMemo(() => currentRows.map(d => d.languageCode), [currentRows]);

  const languageOptions = useMemo(
    () =>
      languagesData?.listLanguages.items.map(
        lang =>
          ({
            value: lang.code,
            label: lang.displayName,
          }) as ComboboxItem,
      ) ?? [],
    [languagesData?.listLanguages.items],
  );

  const fieldErrors = useMemo(
    () =>
      errors.definitions as
        | Array<{ languageCode?: { message?: string }; content?: { message?: string } } | undefined>
        | undefined,
    [errors.definitions],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-7">Definitions</span>
        {fields.length === 0 && (
          <Button
            disabled={disabledButton}
            size="sm"
            variant="outline"
            onClick={() => append({ languageCode: '', content: '' })}
          >
            Add
          </Button>
        )}
      </div>

      {fields.map((field, index) => {
        const rowCode: string = currentRows[index]?.languageCode ?? '';
        // Always include the current row's language even if languageOptions hasn't loaded yet,
        // so the Select shows the existing value instead of appearing blank on edit open.
        const rowOption =
          rowCode && !languageOptions.some(opt => opt.value === rowCode) ? [{ value: rowCode, label: rowCode }] : [];
        const availableOptions = [
          ...rowOption,
          ...languageOptions.filter(opt => opt.value === rowCode || !selectedCodes.includes(opt.value)),
        ];

        return (
          <div key={field.id} className="flex items-start gap-2">
            <div className="w-40 shrink-0">
              <Select
                data={availableOptions}
                disabled={disabledButton}
                error={fieldErrors?.[index]?.languageCode?.message}
                placeholder="Language"
                value={rowCode}
                onChange={val => setValue(`definitions.${index}.languageCode`, val ?? '', { shouldValidate: true })}
              />
            </div>
            <div className="flex-1">
              {useMarkdown ? (
                <MarkdownTextarea
                  {...register(`definitions.${index}.content`)}
                  disabled={disabledButton}
                  error={fieldErrors?.[index]?.content?.message}
                  initialState={
                    currentRows[index]?.content ? MarkdownTextareaMode.PREVIEW : MarkdownTextareaMode.EDITOR
                  }
                  placeholder="Content"
                  useMarkdown={true}
                  value={currentRows[index]?.content ?? ''}
                />
              ) : (
                <Textarea
                  {...register(`definitions.${index}.content`)}
                  autosize={true}
                  disabled={disabledButton}
                  error={fieldErrors?.[index]?.content?.message}
                  minRows={1}
                  placeholder="Content"
                />
              )}
            </div>
            <ActionIcon aria-label="Delete definition" disabled={disabledButton} onClick={() => remove(index)}>
              <MaterialIcons iconName="delete" size={20} className="cursor-pointer" />
            </ActionIcon>
          </div>
        );
      })}
      {fields.length > 0 && fields.length < languageOptions.length && (
        <Button
          disabled={disabledButton}
          size="sm"
          variant="outline"
          onClick={() => append({ languageCode: '', content: '' })}
        >
          Add
        </Button>
      )}
    </div>
  );
};
